import { isOperator } from "../components/process-design/formula/Formulas";

export const nextReferenceType = (cell) => {
  if (/^[A-Z]+\d+$/.test(cell)) {
    return `$${cell[0]}$${cell.slice(1)}`; // A1 -> $A$1
  } else if (/^\$[A-Z]+\$\d+$/.test(cell)) {
    return `${cell[1]}$${cell.match(/\d+$/)[0]}`; // $A$1 -> A$1
  } else if (/^[A-Z]+\$\d+$/.test(cell)) {
    return `$${cell[0]}${cell.match(/\d+$/)[0]}`; // A$1 -> $A1
  } else if (/^\$[A-Z]+\d+$/.test(cell)) {
    return cell.replace("$", ""); // $A1 -> A1
  }
  return cell;
};
export const cycleCellReferenceType = (reference) => {
  if (reference.includes(":")) {
    const [start, end] = reference.split(":");
    return `${nextReferenceType(start)}:${nextReferenceType(end)}`;
  } else {
    return nextReferenceType(reference);
  }
};

export const columnToIndex = (column) => {
  if (column.length === 0) return 0; // Handle empty string case
  if (!/^[A-Za-z]+$/.test(column)) {
    throw new Error("Invalid column value. Only alphabetical letters are allowed.");
  }
  column = column.toUpperCase();
  let index = 0;
  for (let i = 0; i < column.length; i++) {
    const charCode = column.charCodeAt(i) - 64; // Convert character to ASCII code and offset by 64
    index = index * 26 + charCode; // Convert base-26 to base-10
  }
  return index - 1;
};
export const rowToIndex = (row) => {
  return +row - 1;
};
export const indexToColumn = (index) => {
  if (index < 0) {
    throw new RangeError("index must be a non-negative number");
  }
  index = index + 1;
  let result = "";
  while (index > 0) {
    let remainder = (index - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    index = Math.floor((index - 1) / 26);
  }
  return result || index; // in case index is zero for coordinates.
};
export const indexToRow = (index) => {
  return index + 1;
};
export const cellIndexToLabel = (columnIndex, rowIndex) => {
  return `${indexToColumn(columnIndex)}${indexToRow(rowIndex)}`;
};
export const unifyRanges = (range1, range2) => {
  return [
    Math.min(range1[0], range2[0]),
    Math.min(range1[1], range2[1]),
    Math.max(range1[2], range2[2]),
    Math.max(range1[3], range2[3]),
  ];
};
export const intersectRanges = (range1, range2) => {
  const col1 = Math.max(range1[0], range2[0]);
  const row1 = Math.max(range1[1], range2[1]);
  const col2 = Math.min(range1[2], range2[2]);
  const row2 = Math.min(range1[3], range2[3]);

  // Check if the ranges actually intersect
  if (col1 <= col2 && row1 <= row2) {
    return [col1, row1, col2, row2];
  } else {
    // No intersection
    return null;
  }
};
export const rangeDifference = (rangeA, rangeB) => {
  if (!rangeA || !rangeB || !doRangesOverlap(rangeA, rangeB)) return null;

  const removedRanges = [
    // Bottom row - full width
    rangeB[3] < rangeA[3] && [rangeA[0], rangeB[3] + 1, rangeA[2], rangeA[3]],

    // Top row - full width
    rangeB[1] > rangeA[1] && [rangeA[0], rangeA[1], rangeA[2], rangeB[1] - 1],

    // Right column - exclude top and bottom overlaps
    rangeB[2] < rangeA[2] && [rangeB[2] + 1, Math.max(rangeA[1], rangeB[1]), rangeA[2], Math.min(rangeA[3], rangeB[3])],

    // Left column - exclude top and bottom overlaps
    rangeB[0] > rangeA[0] && [rangeA[0], Math.max(rangeA[1], rangeB[1]), rangeB[0] - 1, Math.min(rangeA[3], rangeB[3])],
  ].filter(Boolean); // Filter out any falsey values

  return removedRanges.length ? removedRanges : null;
};
// extract words '_' and '.' will be part of the word
export const extractWords = (text) => {
  // Get words that can contain underscores and dots
  const words = text
    .toLowerCase()
    .split(/[^a-zA-Z0-9._]+/) // Split by anything that's not alphanumeric, dot, or underscore
    .filter((word) => word && word.length > 0); // Remove empty strings

  return [...new Set(words)]; // Remove duplicates
};
export const splitRange = (range) => {
  return range
    .match(/\$?[A-Za-z]+|\$?\d+|:/g)
    .map((match) => {
      // Remove $ sign if present
      match = match.replace("$", "");
      // Convert letters to uppercase
      return match.match(/[A-Za-z]+/) ? match.toUpperCase() : match;
    })
    .filter((x) => x !== ":");
};
export const isCell = (token) => {
  return new RegExp(/^\$?[a-zA-Z]+\$?[0-9]+$/).test(token);
};
export const isRange = (token) => {
  return new RegExp(/^\$?[a-zA-Z]+\$?[0-9]+:\$?[a-zA-Z]+\$?[0-9]+$/).test(token);
};
export const isFormula = (text) => {
  return new RegExp(/^=[A-Za-z0-9$@[\]:#].*/).test(text);
};
export const formatNumberToFitColumn = (value, columnWidth, ctx) => {
  const averageCharWidth = ctx.measureText("0123456789").width / 10;
  const maxChars = Math.floor(columnWidth / averageCharWidth);

  const integerPart = Math.floor(Math.abs(value)).toString();
  const integerLength = integerPart.length + (value < 0 ? 1 : 0);

  const availableDecimals = Math.max(0, maxChars - integerLength - 8);
  const decimalToShow = availableDecimals < 9 ? availableDecimals : 9;

  return parseFloat(value).toFixed(decimalToShow);
};
export const isFormulaComplete = (formula) => {
  let quotes = false,
    balance = 0;
  for (const char of formula) {
    if (char === '"') quotes = !quotes;
    if (!quotes && char === "(") balance++;
    if (!quotes && char === ")") balance--;
  }
  return balance === 0;
};
export const autoCompleteFormula = (formula) => {
  let quotes = false,
    balance = 0;
  for (const char of formula) {
    if (char === '"') quotes = !quotes;
    if (!quotes && char === "(") balance++;
    if (!quotes && char === ")") balance--;
  }
  return balance > 0 ? formula + ")".repeat(balance) : formula;
};
export const isRangeSelectionAllowed = (text) => {
  text = text.trim();
  const lastChar = text[text.length - 1];

  return isOperator(lastChar) || ["(", "="].includes(lastChar);
};
export const setProperty = (obj, id, key, value) => {
  if (!obj[id]) {
    obj[id] = {};
  }
  obj[id][key] = value;
};

export const isDigit = (data) => {
  return new RegExp(/^-?\d+$/).test(data);
};

export const getRangesFromAString = (title) => {
  // Updated regex to include optional sheet names
  const regex =
    /^(?:[A-Za-z0-9_]+!)?\$?[A-Za-z]+\$?\d+(?::\$?[A-Za-z]+\$?\d+)?(?:,\s*(?:[A-Za-z0-9_]+!)?\$?[A-Za-z]+\$?\d+(?::\$?[A-Za-z]+\$?\d+)?)*$/;
  return (title + "").split(/[\s(),.=+=*/^%<>&]/).filter((item) => item.match(regex));
};

export const getMatrixOfValue = (range, value) => {
  const cells = [];
  for (let i = range[1]; i <= range[3]; i++) {
    cells.push([]);
    for (let j = range[0]; j <= range[2]; j++) {
      cells[cells.length - 1].push(value);
    }
  }
  return cells;
};

const isMacOS =
  (typeof navigator !== "undefined" ? navigator.platform : process.platform).toUpperCase().indexOf("MAC") >= 0;
export const getShortcutText = (key) => {
  if (key == "ctrl+.") return key;
  return isMacOS ? key.replace("Ctrl", "⌘").replace("ctrl", "⌘") : key;
};

export const isCellWithinRange = (cell, range) => {
  return cell[0] >= range[0] && cell[0] <= range[2] && cell[1] >= range[1] && cell[1] <= range[3];
};

export const isRangeWithinRange = (childRange, parentRange) => {
  return (
    isCellWithinRange([childRange[0], childRange[1]], parentRange) &&
    isCellWithinRange([childRange[2], childRange[3]], parentRange)
  );
};

export const doRangesOverlap = (range1, range2) => {
  // If one range is completely to the left of the other
  if (range1[2] < range2[0] || range2[2] < range1[0]) {
    return false;
  }

  // If one range is completely above the other
  if (range1[3] < range2[1] || range2[3] < range1[1]) {
    return false;
  }

  // Otherwise, the ranges overlap
  return true;
};
export const isRangeBelowAnotherRange = (childRange, parentRange) => {
  // Check if the start row of the child range is greater than the end row of the parent range
  return childRange[1] > parentRange[3];
};
export const isRangeToLeftOfAnotherRange = (childRange, parentRange) => {
  // Check if the end column of the child range is less than the start column of the parent range
  return childRange[2] < parentRange[0];
};
export const updateRange = (range, direction, steps = 1) => {
  let [startCol, startRow, endCol, endRow] = range;

  switch (direction) {
    case "up":
      startRow -= steps;
      endRow -= steps;
      break;
    case "down":
      startRow += steps;
      endRow += steps;
      break;
    case "left":
      startCol -= steps;
      endCol -= steps;
      break;
    case "right":
      startCol += steps;
      endCol += steps;
      break;
    default:
      throw new Error("Invalid direction. Use 'up', 'down', 'left', or 'right'.");
  }

  // Check if r1 and c1 are less than 0
  if (startRow < 0 || startCol < 0) {
    return null;
  }

  return [startCol, startRow, endCol, endRow];
};
export const bothRangeInSameRow = (range1, range2) => {
  if (range1[1] === range2[1] && range1[3] === range2[3]) {
    return true;
  }
  return false;
};
export const bothRangeInSameColumns = (range1, range2) => {
  if (range1[0] === range2[0] && range1[2] === range2[2]) {
    return true;
  }
  return false;
};
export const getRowRange = (tableRange, cellRange) => {
  // Check if the cell is within the table range
  if (!isCellWithinRange(cellRange, tableRange)) {
    return null;
  }

  // Compute the row range
  return [
    cellRange[1], // start row
    tableRange[0], // start column
    cellRange[1], // end row
    tableRange[2], // end column
  ];
};

export const getColumnRange = (tableRange, cellRange) => {
  // Check if the cell is within the table range
  if (!isCellWithinRange(cellRange, tableRange)) {
    return null;
  }
  return [
    cellRange[0], // start column
    tableRange[1], // start row
    cellRange[2], // end column
    tableRange[3], // end row
  ];
};
export const deepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;

  if (obj1 == null || obj2 == null || typeof obj1 !== "object" || typeof obj2 !== "object") {
    return false;
  }

  let keys1 = Object.keys(obj1);
  let keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (let key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
};
export const getColumnFromMatrix = (matrix, columnIndex) => {
  return matrix.map((row) => row[columnIndex]);
};
export const replaceAroundCursor = (text, cursorPos, oldStr, newStr) => {
  // Check if oldStr is in the text
  if (!text.includes(oldStr)) {
    return { newText: text, cursorPos };
  }

  // Find all occurrences of oldStr
  let occurrences = [];
  let index = text.indexOf(oldStr);
  while (index !== -1) {
    occurrences.push(index);
    index = text.indexOf(oldStr, index + 1);
  }

  if (occurrences.length === 0) {
    return { newText: text, cursorPos };
  }
  // Adjust cursor position if it was after the replaced substring
  cursorPos += newStr.length - oldStr.length;

  // If there's only one occurrence, replace it
  if (occurrences.length === 1) {
    return { newText: text.replace(oldStr, newStr), cursorPos };
  }

  // Find the nearest occurrence to the cursor position
  let nearest = occurrences.reduce((a, b) => (Math.abs(b - cursorPos) < Math.abs(a - cursorPos) ? b : a));

  // Replace the nearest occurrence
  let before = text.slice(0, nearest);
  let after = text.slice(nearest + oldStr.length);
  let newText = before + newStr + after;
  return { newText, cursorPos };
};

export const getKeyByValue = (map, value) => {
  for (let [key, val] of Object.entries(map)) {
    if (deepEqual(val, value)) {
      return key;
    }
  }
  return null;
};

export const convertToAbsolute = (range, options = null) => {
  if (options === null) {
    options = { col: true, row: true }; // Default behavior: make both column and row absolute
  }

  // Regular expression to match the cell reference (e.g., A1, B2)
  const cellRegex = /(\$?)([A-Z]+)(\$?)(\d+)/g;

  // Replace each match with its absolute or relative equivalent based on the options
  return range.replace(cellRegex, (match, colDollar, col, rowDollar, row) => {
    const absCol = options.col ? `$${col}` : col; // Make column absolute if options.col is true
    const absRow = options.row ? `$${row}` : row; // Make row absolute if options.row is true

    // Return the formatted cell reference based on options
    return `${absCol}${absRow}`;
  });
};
export const checkIfAbsolute = (range) => {
  // Regular expression to match the cell reference (e.g., $A$1, A$1, $A1, A1)
  const cellRegex = /(\$?)([A-Z]+)(\$?)(\d+)/g;

  let result = {
    column: true, // Track if all columns are absolute
    row: true, // Track if all rows are absolute
  };

  // Find and process each cell reference in the range (e.g., A10, B12)
  let match;
  while ((match = cellRegex.exec(range)) !== null) {
    const [fullMatch, colDollar, col, rowDollar, row] = match;

    // Check if column is absolute
    const isColumnAbsolute = !!colDollar;
    // Check if row is absolute
    const isRowAbsolute = !!rowDollar;

    // If any column is not absolute, set column to false
    if (!isColumnAbsolute) {
      result.column = false;
    }

    // If any row is not absolute, set row to false
    if (!isRowAbsolute) {
      result.row = false;
    }

    // If both column and row become non-absolute at any point, no need to continue checking
    if (!result.column && !result.row) {
      break;
    }
  }

  return result;
};
export const getRefrenceAroundCursor = (text, cursorPosition, extraFormats = []) => {
  let formats = [
    "Table[#Header]",
    "Table[Column]",
    "Table[[#Header],[Column]:[Column]]",
    "Table[[Column]:[Column]]",
    "Table[$Column]",
    "Table[@$Column]",
    "Table[@Column]",
    "Table[@$[Column]:[Column]]",
    "Table[@[Column]:[Column]]",
    "Table[$[Column]:[Column]]",
    "Table[[#Header],$[Column]]",
    "Table[[#Header],[Column]]",
    "Table[[#Header],$[Column]:[Column]]",
  ];

  formats.push(...extraFormats);
  const formatPattern = formats
    .map((format) => {
      return format
        .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
        .replace(/(\w+)/g, "([A-Za-z0-9_$]+)")
        .replace(/\\\[/g, "\\[")
        .replace(/\\\]/g, "\\]");
    })
    .join("|");

  const regex = new RegExp(formatPattern, "g");
  let match;

  while ((match = regex.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[0].length;

    if (cursorPosition >= start && cursorPosition <= end) {
      return match[0];
    }
  }

  return "";
};

export const findWordOccurrenceAtIndex = (text, word, index) => {
  if (!word) return null; // Prevent infinite loop if `word` is empty

  const regex = new RegExp(`\\b${word}\\b`, "gi");
  let match;
  let occurrenceNumber = 0;

  while ((match = regex.exec(text)) !== null) {
    occurrenceNumber++;
    if (index >= match.index && index <= match.index + word.length) {
      return occurrenceNumber;
    }
  }
  return null;
};

export const hasDuplicates = (array) => {
  const uniqueElements = new Set(array);
  return uniqueElements.size !== array.length;
};

export const containsFormula = (data) => {
  // Loop through the 2D array
  for (let i = 0; i < data.length; i++) {
    for (let j = 0; j < data[i].length; j++) {
      // Check if the current item starts with '='
      if (typeof data[i][j] === "string" && data[i][j].trim().startsWith("=")) {
        return true; // Return true if any item starts with '='
      }
    }
  }
  return false; // Return false if no item starts with '='
};

// for case wher both key and value are getting updated frequently
class BiMap {
  constructor() {
    this.keyToValue = new Map();
    this.valueToKey = new Map();
  }

  set(key, value) {
    this.keyToValue.set(key, value);
    this.valueToKey.set(value, key);
  }

  getByKey(key) {
    return this.keyToValue.get(key);
  }

  getByValue(value) {
    return this.valueToKey.get(value);
  }

  deleteByKey(key) {
    const value = this.keyToValue.get(key);
    this.keyToValue.delete(key);
    this.valueToKey.delete(value);
  }

  deleteByValue(value) {
    const key = this.valueToKey.get(value);
    this.valueToKey.delete(value);
    this.keyToValue.delete(key);
  }

  updateByKey(oldKey, newKey, newValue) {
    const oldValue = this.getByKey(oldKey);
    if (oldValue !== undefined) {
      this.deleteByKey(oldKey); // Remove the old key-value pair
      this.set(newKey, newValue); // Add the new key-value pair
    }
  }

  updateByValue(oldValue, newKey, newValue) {
    const oldKey = this.getByValue(oldValue);
    if (oldKey !== undefined) {
      this.deleteByValue(oldValue); // Remove the old value-key pair
      this.set(newKey, newValue); // Add the new key-value pair
    }
  }

  // Getters for the internal Maps
  getKeyToValueMap() {
    return this.keyToValue;
  }

  getValueToKeyMap() {
    return this.valueToKey;
  }
}

export { BiMap };

export const removeTextFromEnd = (str, textToRemove) => {
  if (str.toLowerCase().endsWith(textToRemove) && textToRemove.length > 0) {
    const regex = new RegExp(textToRemove + "$", "i"); // 'i' for case-insensitive match, '$' ensures it matches only at the end
    return str.replace(regex, "").trimEnd();
  }
  return str;
};

export const compareNodes = (localNodes, remoteNodes) => {
  const added = localNodes.filter((ln) => !remoteNodes.some((rn) => rn.id === ln.id));
  const removed = remoteNodes.filter((rn) => !localNodes.some((ln) => ln.id === rn.id));
  return { added, removed };
};
// export structure Table Utils
export * from "./structureTableUtils.js";
